// src/components/SalaryHistory.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  LinearProgress,
  Fade,
  Zoom,
  Badge,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  CardGiftcard as BonusIcon,
  RemoveCircle as DeductIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  Timeline as TimelineIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

// Import layout components
import Sidebar from '../components/sidebars/Sidebar_staff';
import TopNav from '../components/top_bars/TopNav';
import StatisticsCards from '../components/top_bars/StatisticsCard';

// ==================== CONSTANTS ====================
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const ITEMS_PER_PAGE = 10;

// ==================== HELPER FUNCTIONS ====================

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('[SalaryHistory] Date formatting error:', error);
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('[SalaryHistory] DateTime formatting error:', error);
    return 'N/A';
  }
};

const formatMonthYear = (month, year) => {
  if (!month || !year) return 'N/A';
  const monthNum = parseInt(month);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return `${month} ${year}`;
  return `${MONTHS[monthNum - 1]} ${year}`;
};

const getStatusColor = (status) => {
  const statusLower = String(status).toLowerCase();
  if (statusLower === 'approved' || statusLower === 'paid' || statusLower === '1') return '#4caf50';
  if (statusLower === 'pending' || statusLower === '0') return '#ff9800';
  if (statusLower === 'rejected' || statusLower === '2') return '#f44336';
  return '#757575';
};

const getStatusIcon = (status) => {
  const statusLower = String(status).toLowerCase();
  if (statusLower === 'approved' || statusLower === 'paid') return <CheckCircleIcon fontSize="small" />;
  if (statusLower === 'pending') return <PendingIcon fontSize="small" />;
  if (statusLower === 'rejected') return <CancelIcon fontSize="small" />;
  return null;
};

const getAdjustmentIcon = (type) => {
  if (type === 'increment') return <TrendingUpIcon fontSize="small" />;
  if (type === 'downgrade') return <TrendingDownIcon fontSize="small" />;
  return <HistoryIcon fontSize="small" />;
};

const sanitizeInput = (input) => {
  if (!input) return '';
  if (typeof input !== 'string') return String(input);
  // Remove HTML tags and dangerous characters
  return input.replace(/[<>]/g, '').replace(/&[#\w]+;/g, '').trim();
};

const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  const phoneRegex = /^[0-9+\-\s()]{10,15}$/;
  return phoneRegex.test(phone);
};

const validateEmail = (email) => {
  if (!email) return true;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const escapeHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) {
    console.warn('[SalaryHistory] No data to export');
    return;
  }
  
  const headers = Object.keys(data[0]);
  const csvRows = [];
  csvRows.push(headers.join(','));
  
  for (const row of data) {
    const values = headers.map(header => {
      let value = row[header] || '';
      if (typeof value === 'string') {
        value = value.replace(/"/g, '""');
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }
      return value;
    });
    csvRows.push(values.join(','));
  }
  
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  console.debug(`[SalaryHistory] Exported ${data.length} records to ${filename}.csv`);
};

const printTable = (tableId, title) => {
  const printContent = document.getElementById(tableId);
  if (!printContent) {
    console.error(`[SalaryHistory] Table with id ${tableId} not found`);
    return;
  }
  
  const originalTitle = document.title;
  document.title = title;
  
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('[SalaryHistory] Could not open print window');
    return;
  }
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h2 { color: #333; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .header { text-align: center; margin-bottom: 20px; }
          .date { text-align: right; font-size: 12px; color: #666; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${escapeHtml(title)}</h2>
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>
        <div class="date">Confidential - Staff Salary Report</div>
        ${printContent.cloneNode(true).outerHTML}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.print();
  printWindow.close();
  document.title = originalTitle;
  
  console.debug(`[SalaryHistory] Printed table: ${title}`);
};

// ==================== DETAILS DIALOG COMPONENT ====================
const DetailsDialog = ({ open, onClose, title, data, type }) => {
  const theme = useTheme();
  
  if (!data) return null;
  
  const renderDeductionDetails = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Amount
          </Typography>
          <Typography variant="h6" color="error" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
            -{formatCurrency(data.amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Period
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatMonthYear(data.payroll_month, data.payroll_year)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Reason / Remark
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif', whiteSpace: 'pre-wrap' }}>
              {sanitizeInput(data.remark) || 'No reason provided'}
            </Typography>
          </Paper>
        </Grid>
        {data.rejection_reason && data.deduction_status === 2 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="error" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Rejection Reason
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2, borderColor: '#f44336' }}>
              <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif', whiteSpace: 'pre-wrap' }}>
                {sanitizeInput(data.rejection_reason)}
              </Typography>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Status
          </Typography>
          <Chip 
            label={data.status_text || 'Unknown'}
            icon={getStatusIcon(data.deduction_status)}
            sx={{ 
              bgcolor: alpha(getStatusColor(data.deduction_status), 0.1),
              color: getStatusColor(data.deduction_status),
              fontWeight: 'medium'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Date Created
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatDateTime(data.date_created)}
          </Typography>
        </Grid>
        {data.approved_by_name && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Approved By
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {sanitizeInput(data.approved_by_name)}
            </Typography>
          </Grid>
        )}
        {data.approved_date && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Approved Date
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {formatDateTime(data.approved_date)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
  
  const renderBonusDetails = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Amount
          </Typography>
          <Typography variant="h6" color="success.main" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
            +{formatCurrency(data.bonus_amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Period
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatMonthYear(data.bonus_month, data.bonus_year)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Reason
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif', whiteSpace: 'pre-wrap' }}>
              {sanitizeInput(data.reason) || 'No reason provided'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Bonus Date
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatDate(data.bonus_date)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Payment Status
          </Typography>
          <Chip 
            label={data.is_paid ? 'Paid' : 'Pending'}
            icon={data.is_paid ? <CheckCircleIcon /> : <PendingIcon />}
            sx={{ 
              bgcolor: data.is_paid ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
              color: data.is_paid ? '#4caf50' : '#ff9800'
            }}
          />
        </Grid>
        {data.paid_date && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Paid Date
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {formatDate(data.paid_date)}
            </Typography>
          </Grid>
        )}
        {data.approved_by_name && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Approved By
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {sanitizeInput(data.approved_by_name)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
  
  const renderAdjustmentDetails = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Adjustment Type
          </Typography>
          <Chip 
            label={data.adjustment_type === 'increment' ? 'Salary Increment' : 'Salary Downgrade'}
            icon={getAdjustmentIcon(data.adjustment_type)}
            sx={{ 
              bgcolor: data.adjustment_type === 'increment' ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1),
              color: data.adjustment_type === 'increment' ? '#4caf50' : '#f44336'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Adjustment Amount
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ 
            fontFamily: 'Times New Roman, serif',
            color: data.adjustment_type === 'increment' ? '#4caf50' : '#f44336'
          }}>
            {data.adjustment_type === 'increment' ? '+' : '-'}{formatCurrency(data.amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Previous Salary
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatCurrency(data.previous_salary)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            New Salary
          </Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatCurrency(data.new_salary)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Reason
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ fontFamily: 'Times New Roman, serif', whiteSpace: 'pre-wrap' }}>
              {sanitizeInput(data.reason) || 'No reason provided'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
            Adjustment Date
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {formatDate(data.adjustment_date)}
          </Typography>
        </Grid>
        {data.approved_by_name && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Approved By
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {sanitizeInput(data.approved_by_name)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <InfoIcon color="primary" />
          <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 3 }}>
        {type === 'deduction' && renderDeductionDetails()}
        {type === 'bonus' && renderBonusDetails()}
        {type === 'adjustment' && renderAdjustmentDetails()}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ fontFamily: 'Times New Roman, serif' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== EXPORT DIALOG COMPONENT ====================
const ExportDialog = ({ open, onClose, data, type }) => {
  const [exporting, setExporting] = useState(false);
  
  const handleExportCSV = () => {
    setExporting(true);
    try {
      let exportData = [];
      let filename = '';
      
      if (type === 'deductions' && data?.deduction_history) {
        exportData = data.deduction_history.map(d => ({
          'Period': formatMonthYear(d.payroll_month, d.payroll_year),
          'Amount': d.amount,
          'Reason': d.remark,
          'Status': d.status_text,
          'Date Created': formatDate(d.date_created),
          'Approved By': d.approved_by_name || d.approved_by || 'Pending'
        }));
        filename = `salary_deductions_${new Date().toISOString().split('T')[0]}`;
      } else if (type === 'bonuses' && data?.bonus_history) {
        exportData = data.bonus_history.map(b => ({
          'Period': formatMonthYear(b.bonus_month, b.bonus_year),
          'Amount': b.bonus_amount,
          'Reason': b.reason,
          'Status': b.is_paid ? 'Paid' : 'Pending',
          'Bonus Date': formatDate(b.bonus_date),
          'Paid Date': b.paid_date ? formatDate(b.paid_date) : 'Not paid'
        }));
        filename = `bonus_history_${new Date().toISOString().split('T')[0]}`;
      } else if (type === 'adjustments' && data?.salary_adjustments) {
        exportData = data.salary_adjustments.map(a => ({
          'Date': formatDate(a.adjustment_date),
          'Type': a.adjustment_type === 'increment' ? 'Increment' : 'Downgrade',
          'Amount': a.amount,
          'Reason': a.reason,
          'Previous Salary': a.previous_salary,
          'New Salary': a.new_salary
        }));
        filename = `salary_adjustments_${new Date().toISOString().split('T')[0]}`;
      } else if (type === 'payroll' && data?.payroll_history) {
        exportData = data.payroll_history.map(p => ({
          'Period': formatMonthYear(p.month, p.year),
          'Base Salary': p.base_salary,
          'Total Deductions': p.total_deductions,
          'Total Bonuses': p.total_bonus,
          'Net Salary': p.net_salary,
          'Generated By': p.generated_by,
          'Generated Date': formatDate(p.generated_date)
        }));
        filename = `payroll_history_${new Date().toISOString().split('T')[0]}`;
      }
      
      if (exportData.length > 0) {
        downloadCSV(exportData, filename);
      } else {
        console.warn('[SalaryHistory] No data to export for type:', type);
      }
    } catch (error) {
      console.error('[SalaryHistory] Export error:', error);
    } finally {
      setExporting(false);
      onClose();
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Times New Roman, serif' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DownloadIcon />
          <Typography variant="h6" fontWeight="bold">Export Data</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif', mb: 2 }}>
          Export your {type} data to CSV format for external analysis or record keeping.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: 'Times New Roman, serif' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleExportCSV} 
          variant="contained" 
          disabled={exporting}
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
          sx={{ fontFamily: 'Times New Roman, serif' }}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== MAIN COMPONENT ====================
const StaffSalaryHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [retryCount, setRetryCount] = useState(0);
  
  // Pagination states
  const [deductionsPage, setDeductionsPage] = useState(0);
  const [bonusesPage, setBonusesPage] = useState(0);
  const [adjustmentsPage, setAdjustmentsPage] = useState(0);
  const [payrollPage, setPayrollPage] = useState(0);
  
  // Sorting states
  const [deductionsOrderBy, setDeductionsOrderBy] = useState('date_created');
  const [deductionsOrder, setDeductionsOrder] = useState('desc');
  const [bonusesOrderBy, setBonusesOrderBy] = useState('bonus_date');
  const [bonusesOrder, setBonusesOrder] = useState('desc');
  const [adjustmentsOrderBy, setAdjustmentsOrderBy] = useState('adjustment_date');
  const [adjustmentsOrder, setAdjustmentsOrder] = useState('desc');
  
  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    title: '',
    data: null,
    type: null
  });
  const [exportDialog, setExportDialog] = useState({
    open: false,
    type: null
  });
  
  // Current user info from localStorage
  const [currentUser, setCurrentUser] = useState({
    phone: null,
    userId: null,
    role: null,
    name: null,
    email: null
  });

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    console.debug('[SalaryHistory] API Base URL:', baseUrl);
    return baseUrl;
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchSalaryHistory = useCallback(async (isRetry = false) => {
    console.debug('[SalaryHistory] Fetching salary history...', isRetry ? `(Retry attempt ${retryCount + 1})` : '');
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = getApiBaseUrl();
      const phone = localStorage.getItem('phoneNumber');
      const userId = localStorage.getItem('userId');
      const authToken = localStorage.getItem('authToken');
      
      // Input validation
      if (!phone && !userId) {
        throw new Error('User not authenticated. Please login again.');
      }
      
      // Validate phone format if provided
      if (phone && !validatePhoneNumber(phone)) {
        console.warn('[SalaryHistory] Invalid phone number format:', phone);
      }
      
      // Validate email if provided
      const email = localStorage.getItem('email');
      if (email && !validateEmail(email)) {
        console.warn('[SalaryHistory] Invalid email format:', email);
      }
      
      // Log the request for debugging (excluding sensitive data)
      console.debug('[SalaryHistory] Request payload:', { 
        phone: phone ? `${phone.substring(0, 4)}****` : null, 
        user_id: userId 
      });
      
      const response = await axios.post(`${baseUrl}/staff_salary_history.php`, {
        phone: phone || null,
        user_id: userId || null
      }, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      console.debug('[SalaryHistory] Response status:', response.status);
      console.debug('[SalaryHistory] Response success:', response.data?.success);
      
      if (response.data && response.data.success) {
        // Validate response data structure
        const responseData = response.data.data || {};
        
        // Sanitize all string fields in the response data
        const sanitizeData = (obj) => {
          if (!obj) return obj;
          if (typeof obj === 'string') return sanitizeInput(obj);
          if (Array.isArray(obj)) return obj.map(item => sanitizeData(item));
          if (typeof obj === 'object') {
            const sanitized = {};
            for (const key in obj) {
              if (obj.hasOwnProperty(key)) {
                sanitized[key] = sanitizeData(obj[key]);
              }
            }
            return sanitized;
          }
          return obj;
        };
        
        const sanitizedData = sanitizeData(responseData);
        setData(sanitizedData);
        setRetryCount(0);
        
        // Reset pagination when new data loads
        setDeductionsPage(0);
        setBonusesPage(0);
        setAdjustmentsPage(0);
        setPayrollPage(0);
        
        console.debug('[SalaryHistory] Data loaded successfully');
        
        // Show success message for retry
        if (isRetry) {
          setSnackbar({ 
            open: true, 
            message: 'Salary history loaded successfully!', 
            severity: 'success' 
          });
        }
      } else {
        throw new Error(response.data?.message || 'Failed to load salary history');
      }
    } catch (err) {
      console.error('[SalaryHistory] Error:', err);
      
      // Handle network errors with retry logic
      if (!isRetry && retryCount < MAX_RETRY_ATTEMPTS && (err.code === 'ECONNABORTED' || err.message === 'Network Error')) {
        console.debug(`[SalaryHistory] Retrying... Attempt ${retryCount + 1} of ${MAX_RETRY_ATTEMPTS}`);
        setRetryCount(prev => prev + 1);
        await delay(RETRY_DELAY_MS);
        return fetchSalaryHistory(true);
      }
      
      // Extract more detailed error message
      let errorMessage = 'An unexpected error occurred';
      if (err.response?.data?.message) {
        errorMessage = sanitizeInput(err.response.data.message);
      } else if (err.response?.data?.error) {
        errorMessage = sanitizeInput(err.response.data.error);
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request. Please check your login status.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
        // Clear local storage on auth error
        localStorage.removeItem('phoneNumber');
        localStorage.removeItem('userId');
        localStorage.removeItem('authToken');
        localStorage.removeItem('role');
        localStorage.removeItem('userName');
        localStorage.removeItem('email');
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to view salary history.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Salary history service not found. Please contact support.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your internet connection.';
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (err.message) {
        errorMessage = sanitizeInput(err.message);
      }
      
      setError(errorMessage);
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    console.debug('[SalaryHistory] Component mounted, initializing...');
    
    // Get current user info from localStorage with validation
    const phone = localStorage.getItem('phoneNumber');
    const userId = localStorage.getItem('userId');
    const role = localStorage.getItem('role');
    const userName = localStorage.getItem('userName');
    const email = localStorage.getItem('email');
    
    // Validate required fields
    if (!phone && !userId) {
      console.warn('[SalaryHistory] No authentication credentials found');
      setError('Please login to view your salary history');
      setLoading(false);
      return;
    }
    
    setCurrentUser({ 
      phone: phone ? sanitizeInput(phone) : null, 
      userId: userId ? sanitizeInput(userId) : null, 
      role: role ? sanitizeInput(role) : null, 
      name: userName ? sanitizeInput(userName) : null,
      email: email ? sanitizeInput(email) : null
    });
    
    fetchSalaryHistory();
    
    // Cleanup function
    return () => {
      console.debug('[SalaryHistory] Component unmounting...');
    };
  }, [fetchSalaryHistory]);

  // ==================== HANDLERS ====================
  const handleViewDetails = (item, type) => {
    console.debug(`[SalaryHistory] Viewing ${type} details for ID:`, item.id);
    
    let title = '';
    switch (type) {
      case 'deduction':
        title = 'Salary Deduction Details';
        break;
      case 'bonus':
        title = 'Bonus Details';
        break;
      case 'adjustment':
        title = 'Salary Adjustment Details';
        break;
      default:
        title = 'Details';
    }
    
    setDetailsDialog({
      open: true,
      title,
      data: item,
      type
    });
  };
  
  const handleExport = (type) => {
    console.debug(`[SalaryHistory] Opening export dialog for type: ${type}`);
    setExportDialog({
      open: true,
      type
    });
  };
  
  const handleRefresh = () => {
    console.debug('[SalaryHistory] Manual refresh triggered');
    fetchSalaryHistory();
  };
  
  const handleSort = (field, type) => {
    console.debug(`[SalaryHistory] Sorting ${type} by ${field}`);
    
    switch (type) {
      case 'deductions':
        if (deductionsOrderBy === field) {
          setDeductionsOrder(deductionsOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setDeductionsOrderBy(field);
          setDeductionsOrder('asc');
        }
        break;
      case 'bonuses':
        if (bonusesOrderBy === field) {
          setBonusesOrder(bonusesOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setBonusesOrderBy(field);
          setBonusesOrder('asc');
        }
        break;
      case 'adjustments':
        if (adjustmentsOrderBy === field) {
          setAdjustmentsOrder(adjustmentsOrder === 'asc' ? 'desc' : 'asc');
        } else {
          setAdjustmentsOrderBy(field);
          setAdjustmentsOrder('asc');
        }
        break;
      default:
        break;
    }
  };

  // ==================== FILTERED AND SORTED DATA ====================
  const getSortedData = (dataArray, orderBy, order) => {
    if (!dataArray || dataArray.length === 0) return [];
    
    return [...dataArray].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      
      // Handle dates
      if (orderBy === 'date_created' || orderBy === 'approved_date' || 
          orderBy === 'adjustment_date' || orderBy === 'bonus_date' ||
          orderBy === 'paid_date') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      
      // Handle numbers
      if (orderBy === 'amount' || orderBy === 'bonus_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Handle strings
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  const filteredDeductions = useMemo(() => {
    if (!data?.deduction_history) return [];
    let filtered = data.deduction_history;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        (d.remark || '').toLowerCase().includes(term) ||
        (d.status_text || '').toLowerCase().includes(term) ||
        formatMonthYear(d.payroll_month, d.payroll_year).toLowerCase().includes(term) ||
        (d.approved_by_name || '').toLowerCase().includes(term)
      );
    }
    
    return getSortedData(filtered, deductionsOrderBy, deductionsOrder);
  }, [data?.deduction_history, searchTerm, deductionsOrderBy, deductionsOrder]);

  const filteredBonuses = useMemo(() => {
    if (!data?.bonus_history) return [];
    let filtered = data.bonus_history;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        (b.reason || '').toLowerCase().includes(term) ||
        formatMonthYear(b.bonus_month, b.bonus_year).toLowerCase().includes(term) ||
        (b.approved_by_name || '').toLowerCase().includes(term)
      );
    }
    
    return getSortedData(filtered, bonusesOrderBy, bonusesOrder);
  }, [data?.bonus_history, searchTerm, bonusesOrderBy, bonusesOrder]);

  const filteredAdjustments = useMemo(() => {
    if (!data?.salary_adjustments) return [];
    let filtered = data.salary_adjustments;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        (a.reason || '').toLowerCase().includes(term) ||
        (a.adjustment_type || '').toLowerCase().includes(term) ||
        (a.approved_by_name || '').toLowerCase().includes(term)
      );
    }
    
    return getSortedData(filtered, adjustmentsOrderBy, adjustmentsOrder);
  }, [data?.salary_adjustments, searchTerm, adjustmentsOrderBy, adjustmentsOrder]);

  const paginatedDeductions = useMemo(() => {
    const start = deductionsPage * ITEMS_PER_PAGE;
    return filteredDeductions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDeductions, deductionsPage]);
  
  const paginatedBonuses = useMemo(() => {
    const start = bonusesPage * ITEMS_PER_PAGE;
    return filteredBonuses.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredBonuses, bonusesPage]);
  
  const paginatedAdjustments = useMemo(() => {
    const start = adjustmentsPage * ITEMS_PER_PAGE;
    return filteredAdjustments.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAdjustments, adjustmentsPage]);

  // ==================== RENDER FUNCTIONS ====================
  
  const StatCard = ({ title, value, subtitle, color, icon: Icon, trend }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%', 
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, fontFamily: 'Times New Roman, serif' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: color, fontFamily: 'Times New Roman, serif' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {trend > 0 ? 
                  <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50' }} /> : 
                  <TrendingDownIcon sx={{ fontSize: 16, color: '#f44336' }} />
                }
                <Typography variant="caption" sx={{ ml: 0.5, color: trend > 0 ? '#4caf50' : '#f44336' }}>
                  {Math.abs(trend)}% from last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const renderCurrentSalary = () => {
    if (!data?.current_salary) return null;
    
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
            color: 'white'
          }}>
            <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1, fontFamily: 'Times New Roman, serif' }}>
              Current Monthly Salary
            </Typography>
            <Typography variant="h3" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {formatCurrency(data.current_salary.amount)}
            </Typography>
            <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip 
                icon={<BankIcon />} 
                label={data.current_salary.bank_name || 'Bank not set'} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip 
                icon={<ReceiptIcon />} 
                label={`Acct: ${data.current_salary.account_number || 'Not set'}`} 
                size="small" 
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
              Year-to-Date Summary ({new Date().getFullYear()})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  Total Deductions
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#f44336" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  {formatCurrency(data.current_salary.total_deductions_this_year)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  Total Bonuses
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#4caf50" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  {formatCurrency(data.current_salary.total_bonus_this_year)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  Salary Adjustments
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#2196f3" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  {data.current_salary.adjustments_this_year || 0}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderMonthlyBreakdown = () => {
    if (!data?.monthly_breakdown || data.monthly_breakdown.length === 0) return null;
    
    const maxSalary = Math.max(...data.monthly_breakdown.map(m => m.base_salary || 0));
    
    return (
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Monthly Breakdown {new Date().getFullYear()}
          </Typography>
          <Tooltip title="Print monthly breakdown">
            <IconButton onClick={() => printTable('monthly-breakdown-table', 'Monthly Salary Breakdown')} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer id="monthly-breakdown-table">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Month</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Base Salary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Deductions</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Bonuses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Net Salary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.monthly_breakdown.map((month, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{MONTHS[month.month - 1]}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(month.base_salary)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#f44336', fontFamily: 'Times New Roman, serif' }}>
                    -{formatCurrency(month.total_deductions)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#4caf50', fontFamily: 'Times New Roman, serif' }}>
                    +{formatCurrency(month.total_bonus)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(month.net_salary)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderSalaryAdjustments = () => {
    if (!filteredAdjustments.length) {
      return (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            No salary adjustment history found
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={() => handleExport('adjustments')} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => printTable('adjustments-table', 'Salary Adjustment History')} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer id="adjustments-table" sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={adjustmentsOrderBy === 'adjustment_date'}
                    direction={adjustmentsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('adjustment_date', 'adjustments')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={adjustmentsOrderBy === 'adjustment_type'}
                    direction={adjustmentsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('adjustment_type', 'adjustments')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={adjustmentsOrderBy === 'amount'}
                    direction={adjustmentsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('amount', 'adjustments')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Reason</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Previous</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>New</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAdjustments.map((adj) => (
                <TableRow key={adj.id} hover>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatDate(adj.adjustment_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      size="small"
                      label={adj.adjustment_type === 'increment' ? 'Increment' : 'Downgrade'}
                      icon={adj.adjustment_type === 'increment' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      sx={{ 
                        bgcolor: adj.adjustment_type === 'increment' ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1),
                        color: adj.adjustment_type === 'increment' ? '#4caf50' : '#f44336',
                        fontWeight: 'medium'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    fontFamily: 'Times New Roman, serif',
                    color: adj.adjustment_type === 'increment' ? '#4caf50' : '#f44336'
                  }}>
                    {formatCurrency(adj.amount)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={adj.reason || 'No reason provided'}>
                      <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {sanitizeInput(adj.reason || 'No reason').substring(0, 40)}
                        {(adj.reason?.length > 40) ? '...' : ''}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(adj.previous_salary)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                    {formatCurrency(adj.new_salary)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDetails(adj, 'adjustment')}
                        sx={{ color: '#2196f3' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ITEMS_PER_PAGE]}
          component="div"
          count={filteredAdjustments.length}
          rowsPerPage={ITEMS_PER_PAGE}
          page={adjustmentsPage}
          onPageChange={(e, newPage) => setAdjustmentsPage(newPage)}
        />
      </Paper>
    );
  };

  const renderBonuses = () => {
    if (!filteredBonuses.length) {
      return (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            No bonus history found
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={() => handleExport('bonuses')} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => printTable('bonuses-table', 'Bonus History')} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer id="bonuses-table" sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={bonusesOrderBy === 'bonus_year' || bonusesOrderBy === 'bonus_month'}
                    direction={bonusesOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('bonus_date', 'bonuses')}
                  >
                    Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={bonusesOrderBy === 'bonus_date'}
                    direction={bonusesOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('bonus_date', 'bonuses')}
                  >
                    Bonus Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={bonusesOrderBy === 'bonus_amount'}
                    direction={bonusesOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('bonus_amount', 'bonuses')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Paid Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBonuses.map((bonus) => (
                <TableRow key={bonus.id} hover>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {formatMonthYear(bonus.bonus_month, bonus.bonus_year)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatDate(bonus.bonus_date)}</TableCell>
                  <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(bonus.bonus_amount)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={bonus.reason || 'No reason provided'}>
                      <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {sanitizeInput(bonus.reason || 'No reason').substring(0, 50)}
                        {(bonus.reason?.length > 50) ? '...' : ''}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small"
                      label={bonus.is_paid ? 'Paid' : 'Pending'}
                      icon={bonus.is_paid ? <CheckCircleIcon /> : <PendingIcon />}
                      sx={{ 
                        bgcolor: bonus.is_paid ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
                        color: bonus.is_paid ? '#4caf50' : '#ff9800'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {bonus.paid_date ? formatDate(bonus.paid_date) : 'Not yet paid'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDetails(bonus, 'bonus')}
                        sx={{ color: '#4caf50' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ITEMS_PER_PAGE]}
          component="div"
          count={filteredBonuses.length}
          rowsPerPage={ITEMS_PER_PAGE}
          page={bonusesPage}
          onPageChange={(e, newPage) => setBonusesPage(newPage)}
        />
      </Paper>
    );
  };

  const renderDeductions = () => {
    if (!filteredDeductions.length) {
      return (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            No deduction history found
          </Typography>
        </Paper>
      );
    }
    
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={() => handleExport('deductions')} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => printTable('deductions-table', 'Salary Deduction History')} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer id="deductions-table" sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={deductionsOrderBy === 'payroll_year' || deductionsOrderBy === 'payroll_month'}
                    direction={deductionsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('payroll_month', 'deductions')}
                  >
                    Payroll Period
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={deductionsOrderBy === 'amount'}
                    direction={deductionsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('amount', 'deductions')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Reason / Remark</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                  <TableSortLabel
                    active={deductionsOrderBy === 'date_created'}
                    direction={deductionsOrder === 'asc' ? 'asc' : 'desc'}
                    onClick={() => handleSort('date_created', 'deductions')}
                  >
                    Date Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Approved By</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDeductions.map((deduction) => (
                <TableRow key={deduction.id} hover>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {formatMonthYear(deduction.payroll_month, deduction.payroll_year)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#f44336', fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                    -{formatCurrency(deduction.amount)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={deduction.remark || 'No reason provided'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CommentIcon sx={{ fontSize: 16, color: '#757575' }} />
                        <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                          {sanitizeInput(deduction.remark || 'No reason').substring(0, 60)}
                          {(deduction.remark?.length > 60) ? '...' : ''}
                        </Typography>
                      </Box>
                    </Tooltip>
                    {deduction.rejection_reason && deduction.deduction_status === 2 && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontFamily: 'Times New Roman, serif' }}>
                        Rejection reason: {sanitizeInput(deduction.rejection_reason)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small"
                      label={deduction.status_text}
                      icon={getStatusIcon(deduction.deduction_status)}
                      sx={{ 
                        bgcolor: alpha(getStatusColor(deduction.deduction_status), 0.1),
                        color: getStatusColor(deduction.deduction_status),
                        fontWeight: 'medium'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontSize: '0.75rem' }}>
                    {formatDate(deduction.date_created)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {sanitizeInput(deduction.approved_by_name || deduction.approved_by || 'Pending')}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDetails(deduction, 'deduction')}
                        sx={{ color: '#f44336' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ITEMS_PER_PAGE]}
          component="div"
          count={filteredDeductions.length}
          rowsPerPage={ITEMS_PER_PAGE}
          page={deductionsPage}
          onPageChange={(e, newPage) => setDeductionsPage(newPage)}
        />
      </Paper>
    );
  };

  const renderPayrollHistory = () => {
    if (!data?.payroll_history?.length) {
      return (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            No payroll history found
          </Typography>
        </Paper>
      );
    }
    
    const start = payrollPage * ITEMS_PER_PAGE;
    const paginatedPayroll = data.payroll_history.slice(start, start + ITEMS_PER_PAGE);
    
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={() => handleExport('payroll')} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => printTable('payroll-table', 'Payroll History')} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer id="payroll-table" sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Period</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Base Salary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Total Deductions</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Total Bonuses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Net Salary</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Generated By</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Generated Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayroll.map((payroll, idx) => (
                <TableRow key={payroll.id || idx} hover>
                  <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                    {formatMonthYear(payroll.month, payroll.year)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(payroll.base_salary)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#f44336', fontFamily: 'Times New Roman, serif' }}>
                    -{formatCurrency(payroll.total_deductions)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#4caf50', fontFamily: 'Times New Roman, serif' }}>
                    +{formatCurrency(payroll.total_bonus)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                    {formatCurrency(payroll.net_salary)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>
                    {sanitizeInput(payroll.generated_by || 'System')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontSize: '0.75rem' }}>
                    {formatDate(payroll.generated_date)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ITEMS_PER_PAGE]}
          component="div"
          count={data.payroll_history.length}
          rowsPerPage={ITEMS_PER_PAGE}
          page={payrollPage}
          onPageChange={(e, newPage) => setPayrollPage(newPage)}
        />
        
        {/* Expand details for each payroll */}
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
            Detailed Breakdown
          </Typography>
          {data.payroll_history.map((payroll, idx) => (
            <Accordion key={`details-${idx}`} sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'medium' }}>
                  {formatMonthYear(payroll.month, payroll.year)} - {formatCurrency(payroll.net_salary)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {payroll.deduction_details && payroll.deduction_details.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="#f44336" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                        Deduction Details:
                      </Typography>
                      <List dense>
                        {payroll.deduction_details.map((detail, dIdx) => (
                          <ListItem key={dIdx} sx={{ pl: 0 }}>
                            <ListItemText 
                              primary={`${formatCurrency(detail.amount)} - ${sanitizeInput(detail.remark || 'No reason')}`}
                              secondary={detail.approved_by ? `Approved by: ${sanitizeInput(detail.approved_by)}` : null}
                              primaryTypographyProps={{ variant: 'body2', fontFamily: 'Times New Roman, serif' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                  {payroll.bonus_details && payroll.bonus_details.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="#4caf50" gutterBottom sx={{ fontFamily: 'Times New Roman, serif' }}>
                        Bonus Details:
                      </Typography>
                      <List dense>
                        {payroll.bonus_details.map((detail, bIdx) => (
                          <ListItem key={bIdx} sx={{ pl: 0 }}>
                            <ListItemText 
                              primary={`${formatCurrency(detail.amount)} - ${sanitizeInput(detail.reason || 'No reason')}`}
                              secondary={detail.approved_by ? `Approved by: ${sanitizeInput(detail.approved_by)}` : null}
                              primaryTypographyProps={{ variant: 'body2', fontFamily: 'Times New Roman, serif' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>
    );
  };

  // ==================== RENDER ====================
  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: isMobile ? 2 : 3,
            ml: isMobile ? 0 : sidebarOpen ? '240px' : '60px',
            transition: 'margin-left 0.2s ease-in-out',
            width: 'calc(100% - 60px)',
            maxWidth: '1400px',
            mx: 'auto',
          }}
        >
          <TopNav
            onLogout={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('userProfile');
              localStorage.removeItem('phoneNumber');
              localStorage.removeItem('userId');
              window.location.href = '/login';
            }}
            onNotificationClick={() => {
              setSnackbar({ open: true, message: 'No new notifications', severity: 'info' });
            }}
            toggleSidebar={toggleSidebar}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, fontFamily: 'Times New Roman, serif' }}>Loading salary history...</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: isMobile ? 2 : 3,
            ml: isMobile ? 0 : sidebarOpen ? '240px' : '60px',
            transition: 'margin-left 0.2s ease-in-out',
            width: 'calc(100% - 60px)',
            maxWidth: '1400px',
            mx: 'auto',
          }}
        >
          <TopNav
            onLogout={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('userProfile');
              localStorage.removeItem('phoneNumber');
              localStorage.removeItem('userId');
              window.location.href = '/login';
            }}
            onNotificationClick={() => {
              setSnackbar({ open: true, message: 'No new notifications', severity: 'info' });
            }}
            toggleSidebar={toggleSidebar}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column' }}>
            <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 500 }}>
              {error}
            </Alert>
            <Button 
              variant="contained" 
              onClick={() => fetchSalaryHistory()} 
              sx={{ fontFamily: 'Times New Roman, serif' }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box
        sx={{
          flexGrow: 1,
          p: isMobile ? 2 : 3,
          ml: isMobile ? 0 : sidebarOpen ? '240px' : '60px',
          transition: 'margin-left 0.2s ease-in-out',
          width: 'calc(100% - 60px)',
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            console.debug('[SalaryHistory] User logging out...');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            localStorage.removeItem('userId');
            window.location.href = '/login';
          }}
          onNotificationClick={() => {
            setSnackbar({ open: true, message: 'No new notifications', severity: 'info' });
          }}
          toggleSidebar={toggleSidebar}
        />
        
        {/* Page Title with Refresh Button */}
        <Box sx={{ mt: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight="600" color="text.primary" sx={{ fontFamily: 'Times New Roman, serif' }}>
              Salary History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
              Track your salary adjustments, bonuses, and deductions
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Statistics Cards for HR/Admin */}
        {(currentUser.role === 'hr' || currentUser.role === 'admin') && (
          <Box sx={{ mt: 1, mb: 3 }}>
            <StatisticsCards />
          </Box>
        )}

        {/* Employee Info Card */}
        {data?.employee && (
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: '#C84646', width: 64, height: 64 }}>
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                {sanitizeInput(data.employee.name)}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip icon={<BusinessIcon />} label={sanitizeInput(data.employee.department || 'No Department')} size="small" />
                <Chip icon={<EmailIcon />} label={sanitizeInput(data.employee.email || 'No Email')} size="small" />
                <Chip icon={<PhoneIcon />} label={sanitizeInput(data.employee.phone)} size="small" />
                <Chip icon={<CalendarIcon />} label={`Hired: ${formatDate(data.employee.hire_date)}`} size="small" />
              </Stack>
            </Box>
          </Paper>
        )}

        {/* Current Salary Stats */}
        {renderCurrentSalary()}

        {/* Monthly Breakdown */}
        {renderMonthlyBreakdown()}

        {/* Tabs */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newVal) => setActiveTab(newVal)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'white'
            }}
          >
            <Tab label="Salary Adjustments" icon={<HistoryIcon />} iconPosition="start" />
            <Tab label="Bonuses" icon={<BonusIcon />} iconPosition="start" />
            <Tab label="Deductions" icon={<DeductIcon />} iconPosition="start" />
            <Tab label="Payroll History" icon={<ReceiptIcon />} iconPosition="start" />
          </Tabs>
          
          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search by reason, period, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(sanitizeInput(e.target.value))}
              sx={{ width: { xs: '100%', md: 300 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
              {(() => {
                let count = 0;
                if (activeTab === 0) count = filteredAdjustments.length;
                else if (activeTab === 1) count = filteredBonuses.length;
                else if (activeTab === 2) count = filteredDeductions.length;
                else if (activeTab === 3) count = data?.payroll_history?.length || 0;
                return `${count} record${count !== 1 ? 's' : ''}`;
              })()}
            </Typography>
          </Box>
          
          {/* Tab Panels */}
          <Box sx={{ p: 2 }}>
            {activeTab === 0 && renderSalaryAdjustments()}
            {activeTab === 1 && renderBonuses()}
            {activeTab === 2 && renderDeductions()}
            {activeTab === 3 && renderPayrollHistory()}
          </Box>
        </Paper>

        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            &copy; {new Date().getFullYear()} Elegantè IT - Employee Salary Management System
          </Typography>
        </footer>
      </Box>

      {/* Details Dialog */}
      <DetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ ...detailsDialog, open: false })}
        title={detailsDialog.title}
        data={detailsDialog.data}
        type={detailsDialog.type}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog({ open: false, type: null })}
        data={data}
        type={exportDialog.type}
      />

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffSalaryHistory;